import { useEffect, useState } from "react";
import { effectiveCargoCapacity, effectiveCruiseSpeedC, isTransportShipType, SHIP_TYPES } from "../game-data/catalog";
import { useGame, useLiveGameTime } from "../app/GameContext";
import type { CombatOrders, MaterialId, Ship, ShipType } from "../shared/game-types";
import { ActionButton, ChoiceGroup, EmptyState, Meter, PageHeader, formatDeadline, formatDurationWithReal, formatGameDate, formatNumber, labels, progressBetween } from "../components/GameUi";
import { homeBodyId } from "../game-engine/core";

const shipTypes = Object.keys(SHIP_TYPES) as ShipType[];
type FleetWorkspace = "shipyard" | "roster" | "missions" | "combat";

const workspaceDescriptions: Record<FleetWorkspace, string> = {
  shipyard: "选择船型和船厂，查看成本、周期与能力要求。",
  roster: "检查舰船状态、舰体、燃料，并安排维修或补给。",
  missions: "查看所有任务的航线、状态和预计到达时间。",
  combat: "设置交战规则并发射有限军事任务。",
};

export function FleetPage() {
  const { state } = useGame();
  const [workspace, setWorkspace] = useState<FleetWorkspace>("roster");
  if (!state) return null;
  const active = state.missions.filter((mission) => mission.status === "enRoute" || mission.status === "returning").length;
  const damaged = state.ships.filter((ship) => ship.hull < 100 || ship.status === "damaged" || ship.fuel < SHIP_TYPES[ship.type].fuel).length;
  const combatUnlocked = state.civilizations.some((civilization) => civilization.contacted);

  return (
    <div className="page-stack">
      <PageHeader eyebrow="FLEET CONTROL // MISSION PLANNING" title="舰队与任务" description="飞船建造、航行和战斗由服务器按任务完成点结算；客户端只提交命令。" />
      <section className="metric-grid" aria-label="舰队摘要">
        <article className="metric-card"><span>舰船总数</span><strong>{state.ships.length}</strong><small>{state.ships.filter((ship) => ship.status === "idle").length} 艘待命</small></article>
        <article className="metric-card"><span>航行任务</span><strong>{active}</strong><small>{state.missions.length} 项历史任务</small></article>
        <article className="metric-card"><span>维修/补给警报</span><strong>{damaged}</strong><small>船坞使用帝国推进剂库存加注</small></article>
      </section>

      <section className="panel" aria-labelledby="fleet-workspace-heading">
        <div className="section-heading">
          <div><p className="eyebrow">WORKSPACE</p><h2 id="fleet-workspace-heading">舰队工作区</h2></div>
        </div>
        <div className="control-grid">
          <ChoiceGroup label="当前功能" value={workspace} onChange={setWorkspace} variant="cards" options={[
            { value: "roster", label: "舰船名册", description: "状态、位置与维修" },
            { value: "missions", label: "任务状态", description: "航线、进度与报告" },
            { value: "shipyard", label: "建造飞船", description: "船型、成本与船厂" },
            ...(combatUnlocked ? [{ value: "combat" as const, label: "战斗", description: "命令与军事任务" }] : []),
          ]} />
          <small className="field-hint">{workspaceDescriptions[workspace]}</small>
        </div>
      </section>

      <div hidden={workspace !== "shipyard"}><Shipyard /></div>
      <div hidden={workspace !== "roster"}><ShipRoster /></div>
      <div hidden={workspace !== "missions"}><MissionLog /></div>
      <div hidden={workspace !== "combat"}><CombatControl /></div>
    </div>
  );
}

function Shipyard() {
  const { state, secondsPerGameYear } = useGame();
  const [shipType, setShipType] = useState<ShipType>("probe");
  const [bodyId, setBodyId] = useState("");
  if (!state) return null;
  const definition = SHIP_TYPES[shipType];
  const bodies = allBodies(state.systems).filter((body) => {
    const development = state.developments[body.id];
    return development && development.level >= 4 && ((development.installations.industrialBase ?? 0) > 0 || (development.installations.colony ?? 0) > 0);
  });
  const yardBody = bodyId
    ? bodies.find((body) => body.id === bodyId)
    : [...bodies].sort((left, right) => (state.developments[right.id]?.level ?? 0) - (state.developments[left.id]?.level ?? 0))[0];
  const yard = yardBody ? state.developments[yardBody.id] : undefined;
  const missingMaterial = yard && Object.entries(definition.cost.materials).find(([id, amount]) => (
    yard.stockpile.materials[id as MaterialId] < (amount ?? 0)
  ));
  const buildBlocker = !yard
    ? "没有可用船厂"
    : state.player.levels.industry < definition.requiredIndustry
      ? `需要${labels.levels.industry} ${definition.requiredIndustry}级`
      : state.player.levels.navigation < definition.requiredNavigation
        ? `需要${labels.levels.navigation} ${definition.requiredNavigation}级`
        : state.player.levels.military < definition.requiredMilitary
          ? `需要${labels.levels.military} ${definition.requiredMilitary}级`
          : yard.stockpile.energy < definition.cost.energy
            ? `${yardBody?.name ?? "船厂"}的能源不足`
            : missingMaterial
              ? `${yardBody?.name ?? "船厂"}的${labels.materials[missingMaterial[0] as MaterialId]}不足`
              : state.resources.energy.propellant < definition.fuel
                ? "帝国推进剂库存不足"
                : "";
  const canBuild = buildBlocker === "";
  return (
    <section className="panel shipyard-panel" aria-labelledby="shipyard-heading">
      <div className="section-heading"><div><p className="eyebrow">SHIPYARD</p><h2 id="shipyard-heading">建造飞船</h2></div><span className="status-chip">{labels.levels.industry} {state.player.levels.industry}级 / {labels.levels.navigation} {state.player.levels.navigation}级 / {labels.levels.military} {state.player.levels.military}级</span></div>
      <div className="control-grid ship-build-grid">
        <ChoiceGroup className="ship-type-picker" label="船型与成本概览" value={shipType} onChange={setShipType} variant="cards" options={shipTypes.map((id) => {
          const candidate = SHIP_TYPES[id];
          return {
            value: id,
            label: candidate.name,
            description: <><span>{candidate.description}</span><span className="ship-choice-cost">能源 {formatNumber(candidate.cost.energy)} · {formatMaterialCosts(candidate.cost.materials)} · 推进剂 {formatNumber(candidate.fuel)}</span></>,
          };
        })} />
        <div className="shipyard-order">
          {bodies.length > 1 && <ChoiceGroup label="建造地点" value={bodyId} onChange={setBodyId} variant="rows" options={[
            { value: "", label: "自动选择", description: "优先使用发展最高的船厂" },
            ...bodies.map((body) => ({ value: body.id, label: body.name })),
          ]} />}
          <div className="selected-definition" data-selected-ship-type={shipType}>
            <header><div><b>{definition.name}</b><span>{definition.description}</span></div><small>{yardBody ? `扣款船厂：${yardBody.name}` : "尚无可用船厂"}</small></header>
            <dl className="inline-specs ship-build-specs">
              <div><dt>当前有效货舱</dt><dd>{formatNumber(effectiveCargoCapacity({ type: shipType, cargoCapacity: definition.cargoCapacity }, state.player.levels.navigation))}</dd></div>
              <div><dt>当前巡航速度</dt><dd>{formatCruiseSpeed(state.player.levels.navigation, shipType)}</dd></div>
              <div><dt>建造周期</dt><dd>{formatDurationWithReal(definition.buildYears, secondsPerGameYear)}</dd></div>
              <div><dt>解锁要求</dt><dd>{labels.levels.industry} {definition.requiredIndustry}级 · {labels.levels.navigation} {definition.requiredNavigation}级 · {labels.levels.military} {definition.requiredMilitary}级</dd></div>
            </dl>
            <div className="ship-cost-heading"><b>建造成本</b><small>需要 / 当前库存</small></div>
            <div className="resource-costs ship-resource-costs" aria-label={`${definition.name}建造成本与当前库存`}>
              <ShipCostChip tone="energy" label="船厂能源" need={definition.cost.energy} have={yard?.stockpile.energy ?? 0} />
              <ShipCostChip tone="energy" label="推进剂（帝国库存）" need={definition.fuel} have={state.resources.energy.propellant} />
              {Object.entries(definition.cost.materials).map(([id, amount]) => (
                <ShipCostChip key={id} tone="materials" label={labels.materials[id as MaterialId]} need={amount ?? 0} have={yard?.stockpile.materials[id as MaterialId] ?? 0} />
              ))}
            </div>
            <small id="ship-build-status" role="status" aria-live="polite" className={canBuild ? "field-hint" : "danger-text"}>{canBuild ? "库存与能力均满足，可以加入该船厂的建造队列。" : buildBlocker}</small>
          </div>
          <ActionButton className="button primary full" action={{ type: "BUILD_SHIP", shipType, ...(yardBody ? { bodyId: yardBody.id } : {}) }} disabled={!canBuild} describedBy="ship-build-status">开始建造 {definition.name}</ActionButton>
        </div>
      </div>
    </section>
  );
}

function ShipCostChip({ tone, label, need, have }: { tone: "energy" | "materials"; label: string; need: number; have: number }) {
  const short = have < need;
  return <span className={`resource-cost tone-${tone} ${short ? "short" : ""}`} aria-label={`${label}需要 ${need}，库存 ${have}`}><small>{label}</small><b>{formatNumber(need)} / {formatNumber(have)}</b>{short && <em>缺少 {formatNumber(need - have)}</em>}</span>;
}

function ShipRoster() {
  const { state, secondsPerGameYear } = useGame();
  const liveGameTime = useLiveGameTime();
  if (!state) return null;
  return (
    <section className="panel" aria-labelledby="roster-heading">
      <div className="section-heading"><div><p className="eyebrow">SHIP ROSTER</p><h2 id="roster-heading">舰船名册</h2><p className="field-hint">展开任意舰船查看实时位置、飞行速度、燃料、完整度与货舱。</p></div></div>
      {state.ships.length ? <div className="ship-roster">{state.ships.map((ship) => (
        <ShipDetails key={ship.id} ship={ship} liveGameTime={liveGameTime} secondsPerGameYear={secondsPerGameYear} />
      ))}</div> : <EmptyState>尚无舰船。先建造一艘自动探测器。</EmptyState>}
    </section>
  );
}

function ShipDetails({ ship, liveGameTime, secondsPerGameYear }: { ship: Ship; liveGameTime: number; secondsPerGameYear: number }) {
  const { state } = useGame();
  const [draftName, setDraftName] = useState(ship.name);
  useEffect(() => setDraftName(ship.name), [ship.name]);
  if (!state) return null;
  const definition = SHIP_TYPES[ship.type];
  const homeId = homeBodyId(state);
  const cargoCapacity = effectiveCargoCapacity(ship, state.player.levels.navigation);
  const mission = state.missions.find((item) => (
    (item.status === "enRoute" || item.status === "returning") && item.shipIds.includes(ship.id)
  ));
  const missionProgress = mission ? progressBetween(mission.launchedAtGameYear, mission.arrivesAtGameYear, liveGameTime) : 0;
  const location = mission
    ? mission.status === "returning"
      ? `${mission.targetBodyId ? bodyName(state, mission.targetBodyId) : "任务目标"} → ${bodyName(state, mission.originBodyId)}（返航）`
      : `${bodyName(state, mission.originBodyId)} → ${mission.targetBodyId ? bodyName(state, mission.targetBodyId) : "任务目标"}`
    : bodyName(state, ship.locationBodyId);
  const yard = state.developments[ship.locationBodyId];
  const atServiceYard = Boolean(yard && yard.level >= 4 && ((yard.installations.industrialBase ?? 0) > 0 || (yard.installations.colony ?? 0) > 0));
  const needsService = ship.hull < 100 || ship.fuel < definition.fuel;
  const serviceable = !mission && ship.status !== "mission" && ship.status !== "building" && needsService && atServiceYard;
  const canRelocate = !mission && (ship.status === "idle" || ship.status === "damaged") && ship.hull > 0;

  return (
    <details className="ship-card" data-ship-id={ship.id}>
      <summary>
        <span><b>{ship.name}</b><small>{labels.ships[ship.type]} · {location}</small></span>
        <span className="status-chip">{labels.status[ship.status]}</span>
      </summary>
      <div className="ship-detail-grid">
        <div className="ship-detail-wide"><span>当前位置</span><b>{location}</b>{mission && <small>航程进度 {Math.round(missionProgress * 100)}%</small>}{mission && <Meter value={missionProgress} max={1} label={`${ship.name}航程进度`} />}</div>
        <div><span>飞行速度</span><b>{formatCruiseSpeed(state.player.levels.navigation, ship.type)}</b><small>{ship.type === "heavyTransport" ? "同级普通运输船的三分之一；仍随航行科技提升" : "当前航行能力的星际巡航速度"}</small></div>
        <div><span>货舱容量</span><b>{formatNumber(cargoCapacity)}</b>{isTransportShipType(ship.type) && <small>基础 {formatNumber(ship.cargoCapacity)} · 航行 {state.player.levels.navigation}级加成</small>}</div>
        <div><span>燃料</span><b>{formatNumber(ship.fuel)} / {formatNumber(definition.fuel)}</b><Meter value={ship.fuel} max={definition.fuel} label={`${ship.name}燃料`} /></div>
        <div><span>舰体完整度</span><b className={ship.hull < 50 ? "danger-text" : ""}>{formatNumber(ship.hull)}%</b><Meter value={ship.hull} label={`${ship.name}舰体`} /></div>
        {mission && <div className="ship-detail-wide"><span>预计到达</span><b>{formatDeadline(mission.arrivesAtGameYear, liveGameTime, secondsPerGameYear)}</b></div>}
        {ship.status === "building" && ship.completesAtGameYear && <div className="ship-detail-wide"><span>建造完成</span><b>{formatDeadline(ship.completesAtGameYear, liveGameTime, secondsPerGameYear)}</b></div>}
      </div>
      <div className="ship-actions">
        <label><span className="field-label">舰船名称</span><input maxLength={48} value={draftName} onChange={(event) => setDraftName(event.target.value)} /></label>
        <ActionButton className="button compact" action={{ type: "RENAME_SHIP", shipId: ship.id, name: draftName }} disabled={!validShipName(draftName) || draftName.trim() === ship.name}>保存名称</ActionButton>
        <ActionButton className="button compact" action={{ type: "REPAIR_SHIP", shipId: ship.id }} disabled={!serviceable}>{needsService && !atServiceYard ? "需先返航补给" : "维修 / 补给"}</ActionButton>
        {ship.locationBodyId !== homeId && <ActionButton className="button compact primary" action={{ type: "RELOCATE_SHIP", shipId: ship.id, bodyId: homeId }} disabled={!canRelocate}>让 {ship.name} 返回 {bodyName(state, homeId)}</ActionButton>}
      </div>
    </details>
  );
}

function MissionLog() {
  const { state, secondsPerGameYear } = useGame();
  const liveGameTime = useLiveGameTime();
  if (!state) return null;
  return (
    <section className="panel" aria-labelledby="mission-log-heading">
      <div className="section-heading"><div><p className="eyebrow">MISSION STATUS</p><h2 id="mission-log-heading">任务状态</h2></div></div>
      {state.missions.length ? <div className="table-scroll" tabIndex={0}><table><thead><tr><th>任务</th><th>舰船</th><th>航线</th><th>状态</th><th>到达时间</th><th>报告</th></tr></thead><tbody>
        {state.missions.map((mission) => {
          const active = mission.status === "enRoute" || mission.status === "returning";
          const route = mission.status === "returning"
            ? `${mission.targetBodyId ? bodyName(state, mission.targetBodyId) : "任务目标"} → ${bodyName(state, mission.originBodyId)}`
            : `${bodyName(state, mission.originBodyId)} → ${mission.targetBodyId ? bodyName(state, mission.targetBodyId) : "未公开目标"}`;
          return <tr key={mission.id}><td><b>{labels.missions[mission.type]}</b></td><td>{mission.shipIds.length}</td><td>{route}</td><td>{labels.status[mission.status]}</td><td>{active ? formatDeadline(mission.arrivesAtGameYear, liveGameTime, secondsPerGameYear) : formatGameDate(mission.arrivesAtGameYear)}</td><td>{mission.report ?? "—"}</td></tr>;
        })}
      </tbody></table></div> : <EmptyState>尚无任务记录。</EmptyState>}
    </section>
  );
}

function CombatControl() {
  const { state } = useGame();
  const [orders, setOrders] = useState<CombatOrders | null>(null);
  const [civilizationId, setCivilizationId] = useState("");
  const [shipIds, setShipIds] = useState<string[]>([]);
  const [targetBodyId, setTargetBodyId] = useState("");
  useEffect(() => { if (state) setOrders({ ...state.automation.combatOrders }); }, [state]);
  if (!state || !orders) return null;
  const patrols = state.ships.filter((ship) => ship.type === "patrol" && ship.status === "idle");
  const bodies = allBodies(state.systems).filter((body) => (state.developments[body.id]?.level ?? 0) >= 2);
  return (
    <section className="panel danger-zone" aria-labelledby="combat-heading">
      <div className="section-heading"><div><p className="eyebrow">COMBAT DOCTRINE</p><h2 id="combat-heading">战前命令与有限军事任务</h2></div><span className="status-chip">{labels.levels.military} {state.player.levels.military}级</span></div>
      <p>服务器依次模拟探测、拦截、远程、近距、撤退五阶段。战争不会直接生成高额通用资源。</p>
      <div className="control-grid combat-grid">
        <ChoiceGroup label="任务目标" value={orders.objective} onChange={(objective) => setOrders({ ...orders, objective })} variant="cards" options={(Object.entries(objectiveLabels) as Array<[CombatOrders["objective"], string]>).map(([value, label]) => ({ value, label, description: objectiveDescriptions[value] }))} />
        <label><span className="field-label">舰体低于此值撤退</span><input type="number" min={5} max={95} value={orders.retreatAtHull} onChange={(event) => setOrders({ ...orders, retreatAtHull: event.target.valueAsNumber })} /></label>
        <ChoiceGroup label="优先目标" value={orders.priorityTarget} onChange={(priorityTarget) => setOrders({ ...orders, priorityTarget })} options={(Object.entries(targetLabels) as Array<[CombatOrders["priorityTarget"], string]>).map(([value, label]) => ({ value, label }))} />
        <label className="check-row"><input type="checkbox" checked={orders.pursue} onChange={(event) => setOrders({ ...orders, pursue: event.target.checked })} /><span><b>允许追击</b><small>敌方撤退后继续交战</small></span></label>
        <label className="check-row"><input type="checkbox" checked={orders.attackCivilian} onChange={(event) => setOrders({ ...orders, attackCivilian: event.target.checked })} /><span><b>允许攻击非军事设施</b><small>将造成严重外交后果</small></span></label>
        <label className="check-row"><input type="checkbox" checked={orders.waitForReinforcements} onChange={(event) => setOrders({ ...orders, waitForReinforcements: event.target.checked })} /><span><b>等待增援</b><small>延迟拦截以集结舰队</small></span></label>
        <ActionButton className="button" action={{ type: "SET_COMBAT_ORDERS", orders }}>保存战斗命令</ActionButton>
      </div>
      <hr />
      <div className="control-grid combat-launch">
        <ChoiceGroup label="目标文明" value={civilizationId} onChange={setCivilizationId} variant="rows" options={state.civilizations.filter((civilization) => civilization.contacted).map((civilization) => ({ value: civilization.id, label: civilization.name, description: `${labels.levels.civilization} ${civilization.level}级` }))} />
        <fieldset><legend>出动巡逻舰</legend>{patrols.length ? patrols.map((ship) => <label key={ship.id} className="check-row"><input type="checkbox" checked={shipIds.includes(ship.id)} onChange={(event) => setShipIds((current) => event.target.checked ? [...current, ship.id] : current.filter((id) => id !== ship.id))} /><span><b>{ship.name}</b><small>舰体 {ship.hull}% · 燃料 {ship.fuel}</small></span></label>) : <span className="muted">没有空闲巡逻舰</span>}</fieldset>
        <ChoiceGroup label="目标天体（可选）" value={targetBodyId} onChange={setTargetBodyId} variant="rows" options={[{ value: "", label: "由情报自动选择", description: "根据任务目标选择最合适地点" }, ...bodies.map((body) => ({ value: body.id, label: body.name, description: labels.bodyTypes[body.type] }))]} />
        <ActionButton className="button danger-button" action={{ type: "LAUNCH_COMBAT_MISSION", civilizationId, shipIds, ...(targetBodyId ? { targetBodyId } : {}) }} disabled={!civilizationId || shipIds.length === 0} confirm="确认发射有限军事任务？该行动可能升级边境冲突。">发射军事任务</ActionButton>
      </div>
    </section>
  );
}

function allBodies(systems: NonNullable<ReturnType<typeof useGame>["state"]>["systems"]) {
  return systems.filter((system) => system.discovered || system.observed).flatMap((system) => system.bodies);
}

function bodyName(state: NonNullable<ReturnType<typeof useGame>["state"]>, id: string) {
  return allBodies(state.systems).find((body) => body.id === id)?.name ?? "未知目标";
}

function formatMaterialCosts(materials: Partial<Record<MaterialId, number>>) {
  return Object.entries(materials).map(([id, amount]) => `${labels.materials[id as MaterialId]} ${formatNumber(amount ?? 0)}`).join(" + ");
}

function formatCruiseSpeed(navigationLevel: number, shipType: ShipType) {
  const fraction = effectiveCruiseSpeedC(shipType, navigationLevel);
  return fraction < 0.001
    ? `${formatNumber(fraction * 299_792.458, 1)} 千米/秒`
    : `${formatNumber(fraction * 100, fraction < 0.01 ? 2 : 1)}% 光速`;
}

function validShipName(value: string) {
  const name = value.trim();
  return [...name].length >= 1 && [...name].length <= 24 && !/\p{Cc}/u.test(name);
}

const objectiveLabels: Record<CombatOrders["objective"], string> = { escort: "护航", driveOff: "驱逐", breakBlockade: "解除封锁", seizeOutpost: "夺取无人设施", destroyMilitary: "摧毁军事目标", defend: "防守" };
const objectiveDescriptions: Record<CombatOrders["objective"], string> = { escort: "保护运输与殖民航线", driveOff: "迫使对方撤离争议区域", breakBlockade: "打通被封锁的航道", seizeOutpost: "控制无人或轻防御设施", destroyMilitary: "集中打击军事力量", defend: "固守己方节点" };
const targetLabels: Record<CombatOrders["priorityTarget"], string> = { sensors: "传感器", propulsion: "推进系统", missiles: "导弹平台", supply: "补给舰", defense: "防御站" };
